import z from 'zod';
import { zoneSchema } from '../Zone/schemas';
import { taskTemplateSchema } from '../Task/schemas';
import { fileSchema } from '../File/schemas';
import { createListApiSchema } from '../General/schemas';

export const roleCategorySchema = z.enum(['contractor', 'visitor', 'employee', 'trucker', 'system', 'other']);
export const roleAccessTypeSchema = z.enum(['permanent', 'event', 'schedule', 'single']);
export const showAccessCodeSchema = z.enum(['always', 'activeRole', 'never']);
export const scheduleDaySchema = z.enum([
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
]);

export const scheduleTimeRangeSchema = z.object({
	startTime: z.string().optional(),
	endTime: z.string().optional(),
});

export const scheduleEntrySchema = z.object({
	day: scheduleDaySchema,
	startTime: z.string().optional(),
	endTime: z.string().optional(),
	timeRanges: z.array(scheduleTimeRangeSchema).optional(),
});

export const roleSchema = z
	.object({
		id: z.string(),
		name: z.string().optional(),
		code: z.string().optional(),
		permissions: z.array(z.string()).optional(),
		tasks: z.array(z.string()).optional(),
		requiresEventBeforeAccess: z.boolean().optional(),
		category: roleCategorySchema.optional(),
		parentRoleIds: z.array(z.string()).optional(),
		accessSiteIds: z.array(z.string().length(4)),
		permissionSiteIds: z.array(z.string().length(4)),
		zoneIds: z.array(z.string()).optional(),
		emergencyZoneIds: z.array(z.string()).optional(),
		keywords: z.string().optional(),
		isFree: z.boolean().optional(),
		isAdmin: z.boolean(),
		isProtected: z.boolean(),
		showAccessCode: showAccessCodeSchema,
		usesManager: z.boolean(),
		accessType: roleAccessTypeSchema,
		excludeFromChecks: z.boolean(),
		autoArchivalPeriod: z.string(),
		dataRetentionPeriod: z.string(),
		shouldNotRenew: z.boolean().optional(),
		externalId: z.string().optional(),
		autoCheckOut: z.boolean(),
		badgeLayoutId: z.string().optional(),
		autoCheckOutTime: z.coerce.date().optional(),
		showMigrationDays: z.boolean().optional(),
		migrationDays: z.number().optional(),
		sendNotification: z.boolean(),
		schedule: z.array(scheduleEntrySchema),
		hasUsers: z.boolean().optional(),
		taskDetails: z.array(z.lazy(() => taskTemplateSchema)).optional(), // virtual
		zones: z.array(z.lazy(() => zoneSchema)).optional(), // virtual
		badgeLayout: z.lazy(() => fileSchema).optional(), // virtual
	})
	.describe('Role');

export const inviteRolesSchema = z.array(roleSchema).describe('InviteRoles');
export const roleAutoCompleteSchema = z.object({
	_id: z.string(),
	id: z.string(),
	name: z.string(),
	accessSiteIds: z.array(z.string()),
	permissionSiteIds: z.array(z.string()),
	parentRoleIds: z.array(z.string()).optional(),
});

export const roleWithTasksSchema = z.object({
	id: z.string(),
	name: z.string(),
	status: z.string().nullable().optional(),
	showAccessCode: z.string(),
	tasks: z.array(z.lazy(() => taskTemplateSchema)),
	openTaskCount: z.number(),
	closestValidToDate: z.date().nullable(),
	hasExpiryWarningTask: z.boolean(),
});

export const rolesApiResponseSchema = createListApiSchema(roleSchema).describe('RoleList');
export const rolesAutoCompleteApiResponseSchema = z.array(roleAutoCompleteSchema).describe('RoleAutoCompleteList');
export const rolesListResponseSchema = z.array(roleSchema).describe('RolesList');
export const rolesWithTasksResponseSchema = z.object({
	data: z.array(roleWithTasksSchema),
});
